import type { Theme } from '@mui/material/styles';
import {
    getDataChartSeriesColors,
    LINE_CHART_DEFAULT_SERIES_COLORS,
} from '../../themes/tokens/data-chart-tokens';
import {
    buildSparseCurveAxisCategoriesForApex,
    createCurveAxisTooltipXFormatter,
} from '../../utils/chart-axis-date';

/** 内嵌对比图坐标轴标签色（语义 CSS 变量） */
const DASHBOARD_LINE_CHART_LABEL_COLOR = 'var(--text-neutral-500)';
/** 内嵌对比图网格线颜色（语义 CSS 变量） */
const DASHBOARD_LINE_CHART_GRID_COLOR = 'var(--stroke-neutral-800-a25)';
/** 横轴刻度字号（MM/DD 已抽稀，水平展示可读） */
const BUSINESS_LINE_CHART_XAXIS_FONT_SIZE = '12px';
/** 纵轴刻度字号 */
const BUSINESS_LINE_CHART_YAXIS_FONT_SIZE = '10px';

/**
 * 营业折线图横轴 labels 默认项：水平、不自动倾斜；配合抽稀后的 MM/DD 约 7 个刻度。
 */
const BUSINESS_LINE_CHART_XAXIS_LABELS: NonNullable<
    NonNullable<ApexCharts.ApexOptions['xaxis']>['labels']
> = {
    rotate: 0,
    rotateAlways: false,
    hideOverlappingLabels: false,
    trim: false,
    style: {
        colors: DASHBOARD_LINE_CHART_LABEL_COLOR,
        fontSize: BUSINESS_LINE_CHART_XAXIS_FONT_SIZE,
    },
};

/** 历史经营页折线图 Apex 基底（阴影、工具栏、斑马纹等与改版前一致） */
const HISTORY_LINE_CHART_BASE: ApexCharts.ApexOptions = {
    chart: {
        height: 350,
        type: 'line',
        dropShadow: {
            enabled: true,
            color: '#000',
            top: 18,
            left: 7,
            blur: 10,
            opacity: 0.2,
        },
        toolbar: {
            show: true,
            offsetY: 0,
        },
    },
    colors: [...LINE_CHART_DEFAULT_SERIES_COLORS],
    dataLabels: {
        enabled: false,
    },
    stroke: {
        curve: 'smooth',
    },
    title: {
        text: '',
        align: 'left',
    },
    grid: {
        borderColor: '#e7e7e7',
        row: {
            colors: ['transparent', '#f3f3f3'],
            opacity: 0.5,
        },
    },
    markers: {
        size: 1,
    },
    xaxis: {
        categories: [],
    },
    yaxis: {},
    legend: {
        position: 'top',
        horizontalAlign: 'right',
        floating: false,
        offsetY: -10,
        offsetX: 0,
    },
};

/**
 * 历史经营：纵轴与 tooltip 金额两位小数
 */
const formatHistoryYAxisValue = (val: number) => {
    const n = Number(val);
    return Number.isFinite(n) ? n.toFixed(2) : '';
};

/**
 * 内嵌对比图：纵轴标签字符串化
 */
const formatDashboardYAxisValue = (val: number) => {
    const n = Number(val);
    return Number.isFinite(n) ? String(n) : '';
};

/**
 * 浅合并 Apex `options` 与调用方片段（嵌套对象单层合并）。
 * @param base - 基底配置
 * @param extra - 覆盖片段（如页面自定义坐标轴、tooltip）
 */
export function mergeBusinessLineApexOptions(
    base: ApexCharts.ApexOptions,
    extra?: Partial<ApexCharts.ApexOptions>,
): ApexCharts.ApexOptions {
    if (!extra) {
        return base;
    }
    return {
        ...base,
        ...extra,
        chart: extra.chart ? { ...base.chart, ...extra.chart } : base.chart,
        stroke: extra.stroke ? { ...(base.stroke ?? {}), ...extra.stroke } : base.stroke,
        legend: extra.legend ? { ...(base.legend ?? {}), ...extra.legend } : base.legend,
        tooltip: extra.tooltip ? { ...(base.tooltip ?? {}), ...extra.tooltip } : base.tooltip,
        xaxis: extra.xaxis
            ? {
                  ...(base.xaxis ?? {}),
                  ...extra.xaxis,
                  labels: extra.xaxis.labels
                      ? {
                            ...(base.xaxis?.labels ?? {}),
                            ...extra.xaxis.labels,
                            style: extra.xaxis.labels.style
                                ? {
                                      ...(base.xaxis?.labels?.style ?? {}),
                                      ...extra.xaxis.labels.style,
                                  }
                                : base.xaxis?.labels?.style,
                        }
                      : base.xaxis?.labels,
              }
            : base.xaxis,
        yaxis: extra.yaxis ? { ...(base.yaxis ?? {}), ...extra.yaxis } : base.yaxis,
    };
}

/** 折线图横轴解析结果（Apex categories + 可选 tooltip formatter） */
interface ResolvedBusinessLineXAxis {
    categories: string[];
    labelsFormatter?: (
        value: string,
        timestamp: number,
        opts?: { dataPointIndex?: number },
    ) => string;
    tooltipXFormatter?: (val: number, opts?: { dataPointIndex?: number }) => string;
}

/**
 * 解析横轴：优先 `rawXLabels`（MM/DD 或 HH:mm + 抽稀 + tooltip 原始 x），否则使用 `categories` 原样展示。
 */
function resolveBusinessLineXAxis(params: {
    categories?: string[];
    rawXLabels?: string[];
    granularity?: string;
    maxXAxisTicks?: number;
}): ResolvedBusinessLineXAxis {
    const raw = params.rawXLabels;
    if (raw && raw.length > 0) {
        return {
            categories: buildSparseCurveAxisCategoriesForApex({
                rawLabels: raw,
                granularity: params.granularity,
                maxTicks: params.maxXAxisTicks,
            }),
            tooltipXFormatter: createCurveAxisTooltipXFormatter(raw),
        };
    }
    return { categories: params.categories ?? [] };
}

/** {@link createDashboardBusinessLineApexOptions} 入参 */
export interface CreateDashboardBusinessLineApexOptionsParams {
    /** 横轴类目（未传 rawXLabels 时使用，不做格式化与抽稀） */
    categories?: string[];
    /** 原始横轴 x；传入则格式化为 MM/DD 或 HH:mm 并抽稀，tooltip 显示完整原始值 */
    rawXLabels?: string[];
    /** 时间聚合粒度，如 hour / day（配合 rawXLabels） */
    granularity?: string;
    /** 横轴最多刻度数（配合 rawXLabels），默认 7 */
    maxXAxisTicks?: number;
    /** 系列条数，用于默认配色 */
    seriesCount: number;
    /** 图高度；默认 `392`；可用 `'100%'` */
    height?: number | string;
    /** 是否显示图例；默认 `true` */
    showLegend?: boolean;
    /** 线宽；默认 `3` */
    strokeWidth?: number;
    /** 与系列条数一致时优先使用；否则 {@link getDataChartSeriesColors} */
    lineColors?: string[];
    /** 与各线对齐的虚线样式；长度须与系列条数一致 */
    strokeDashArray?: number[];
}

/** {@link createHistoryBusinessLineApexOptions} 入参 */
export interface CreateHistoryBusinessLineApexOptionsParams {
    /** 横轴类目（未传 rawXLabels 时使用） */
    categories?: string[];
    /** 原始横轴日期；传入则 MM/DD + 抽稀 + tooltip 完整日期 */
    rawXLabels?: string[];
    granularity?: string;
    maxXAxisTicks?: number;
    /** 固定像素高度（历史页卡片） */
    height: number;
    /** Apex 图表标题 */
    titleText: string;
    /** 系列条数（配色、虚线校验） */
    seriesCount: number;
    /** 是否显示图例；默认 `true` */
    showLegend?: boolean;
    /** 线宽；不传则仅用平滑曲线默认线宽 */
    strokeWidth?: number;
    lineColors?: string[];
    strokeDashArray?: number[];
}

/**
 * 按预设解析系列色：`lineColors` 合法则用之；否则按 {@link getDataChartSeriesColors} 轮询 {@link CHART_PALETTE_ORDERED}（PC/App 统一）。
 */
export function resolveBusinessLineSeriesColors(params: {
    /** 保留以兼容调用方；配色已与 preset 无关 */
    colorPreset: 'dashboard' | 'history';
    /** 系列条数 */
    seriesCount: number;
    /** 与系列条数一致时优先使用 */
    lineColors?: string[];
}): string[] {
    const { seriesCount, lineColors } = params;
    if (lineColors && lineColors.length === seriesCount) {
        return lineColors;
    }
    return getDataChartSeriesColors(seriesCount);
}

/**
 * 构造「集团营收 / 多门店 / 菜品趋势 / 对比弹窗」等内嵌折线的 Apex 基底 options。
 * 页面差异（字号、tooltip、网格等）请再用 {@link mergeBusinessLineApexOptions} 叠加。
 */
export function createDashboardBusinessLineApexOptions(
    theme: Theme,
    params: CreateDashboardBusinessLineApexOptionsParams,
): ApexCharts.ApexOptions {
    const height = params.height ?? 392;
    const strokeWidth = params.strokeWidth ?? 3;
    const showLegend = params.showLegend !== false;
    const dashOk =
        params.strokeDashArray && params.strokeDashArray.length === params.seriesCount;
    const resolvedColors = resolveBusinessLineSeriesColors({
        colorPreset: 'dashboard',
        seriesCount: params.seriesCount,
        lineColors: params.lineColors,
    });
    const xAxis = resolveBusinessLineXAxis(params);

    return {
        chart: {
            type: 'line',
            height,
            toolbar: { show: false },
            zoom: { enabled: false },
            foreColor: theme.palette.text.primary,
            dropShadow: { enabled: false },
        },
        stroke: {
            curve: 'smooth',
            width: strokeWidth,
            ...(dashOk ? { dashArray: params.strokeDashArray } : {}),
        },
        markers: {
            size: 0,
        },
        colors: resolvedColors,
        dataLabels: { enabled: false },
        xaxis: {
            categories: xAxis.categories,
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
                ...BUSINESS_LINE_CHART_XAXIS_LABELS,
                ...(xAxis.labelsFormatter ? { formatter: xAxis.labelsFormatter } : {}),
            },
        },
        yaxis: {
            labels: {
                style: {
                    colors: DASHBOARD_LINE_CHART_LABEL_COLOR,
                    fontSize: BUSINESS_LINE_CHART_YAXIS_FONT_SIZE,
                },
                formatter: formatDashboardYAxisValue,
            },
        },
        grid: {
            borderColor: DASHBOARD_LINE_CHART_GRID_COLOR,
        },
        legend: {
            show: showLegend,
            position: 'top',
            horizontalAlign: 'right',
            fontSize: '10px',
            labels: {
                colors: 'var(--text-dark)',
            },
        },
        tooltip: {
            shared: true,
            intersect: false,
            theme: theme.palette.mode,
            ...(xAxis.tooltipXFormatter
                ? { x: { formatter: xAxis.tooltipXFormatter } }
                : {}),
        },
        title: { text: '' },
    };
}

/**
 * 构造历史经营页「上月 / 本期」折线的 Apex 基底 options。
 */
export function createHistoryBusinessLineApexOptions(
    theme: Theme,
    params: CreateHistoryBusinessLineApexOptionsParams,
): ApexCharts.ApexOptions {
    const resolvedColors = resolveBusinessLineSeriesColors({
        colorPreset: 'history',
        seriesCount: params.seriesCount,
        lineColors: params.lineColors,
    });
    const dashOk =
        params.strokeDashArray && params.strokeDashArray.length === params.seriesCount;
    const xAxis = resolveBusinessLineXAxis(params);

    return {
        ...HISTORY_LINE_CHART_BASE,
        colors: resolvedColors,
        chart: {
            ...HISTORY_LINE_CHART_BASE.chart,
            height: params.height,
            foreColor: theme.palette.text.primary,
        },
        title: {
            ...HISTORY_LINE_CHART_BASE.title,
            text: params.titleText,
        },
        xaxis: {
            ...HISTORY_LINE_CHART_BASE.xaxis,
            categories: xAxis.categories,
            labels: {
                ...BUSINESS_LINE_CHART_XAXIS_LABELS,
                ...(xAxis.labelsFormatter ? { formatter: xAxis.labelsFormatter } : {}),
            },
        },
        yaxis: {
            labels: {
                formatter: formatHistoryYAxisValue,
            },
        },
        stroke: {
            ...HISTORY_LINE_CHART_BASE.stroke,
            ...(params.strokeWidth != null ? { width: params.strokeWidth } : {}),
            ...(dashOk ? { dashArray: params.strokeDashArray } : {}),
        },
        legend: {
            ...HISTORY_LINE_CHART_BASE.legend,
            show: params.showLegend !== false,
        },
        tooltip: {
            theme: theme.palette.mode,
            y: {
                formatter: formatHistoryYAxisValue,
            },
            ...(xAxis.tooltipXFormatter
                ? { x: { formatter: xAxis.tooltipXFormatter } }
                : {}),
        },
    };
}
